/**
 * Generate a strip of thumbnail data URLs by seeking a <video> element to
 * evenly spaced timestamps and drawing frames to a canvas.
 */
export async function generateVideoThumbnails(
  src: string,
  count = 10,
  thumbWidth = 80,
): Promise<string[]> {
  return new Promise(resolve => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = src;

    const thumbs: string[] = [];
    const canvas = document.createElement('canvas');
    let times: number[] = [];
    let idx = 0;

    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
    };

    const onError = () => {
      cleanup();
      resolve(thumbs);
    };

    video.addEventListener('loadedmetadata', () => {
      const dur = video.duration;
      if (!Number.isFinite(dur) || dur <= 0) {
        onError();
        return;
      }
      const aspect = video.videoWidth > 0 ? video.videoHeight / video.videoWidth : 9 / 16;
      canvas.width = thumbWidth;
      canvas.height = Math.max(1, Math.round(thumbWidth * aspect));
      times = Array.from({ length: count }, (_, i) =>
        count === 1 ? dur / 2 : (dur * i) / (count - 1),
      );
      video.currentTime = times[0];
    });

    video.addEventListener('seeked', () => {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          thumbs.push(canvas.toDataURL('image/jpeg', 0.55));
        } catch {
          // Canvas tainted — abort
          cleanup();
          resolve(thumbs);
          return;
        }
      }
      idx++;
      if (idx < times.length) {
        video.currentTime = times[idx];
      } else {
        cleanup();
        resolve(thumbs);
      }
    });

    video.addEventListener('error', onError);
  });
}
